import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, FileText, CheckCircle2, XCircle, Loader2, AlertTriangle,
  Sparkles, FolderOpen, Trash2,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { shrinkImage } from "@/lib/shrinkImage";   // 8 Oct 2026 — images shrunk before upload
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Pill, EmptyState } from "@/components/ui";

// =============================================================================
// ApIntakeScreen — /ap/intake
// T-086 Phase B step 2 — bulk PDF drop for the manager weekly accounting dump.
//
// Workflow per James 2026-04-25:
//   "managers running accounting and need to do a weeks dump but we would
//    try stay ontop of it"
//   Existing flow: Invoices@coretelgroup.com → Power Automate → SharePoint
//   folder dump. Manager bulk-downloads weekly, drops the lot into here.
//
// What this screen does:
//   1. Drag-drop any number of PDFs (or use file picker)
//   2. Hard cap of 50 per batch (keeps Anthropic costs predictable + UI sane)
//   3. For each file:
//      a. Upload bytes to receipt-captures bucket
//      b. Insert pending_receipts row (status=new, no employee, no project)
//      c. Fire /ai/extract-document to populate ocr_raw_json
//      d. Per-row status pill: queued → uploading → extracting → done | failed
//   4. When all done, "View queue" button jumps to /ap/receipts where
//      manager confirms / codes each row.
//
// Permissions: manager+ only. Field staff use MobileReceiptScreen for snaps.
// =============================================================================

const BUCKET = "receipt-captures";
const MAX_BATCH = 50;
const MAX_FILE_BYTES = 25 * 1024 * 1024;   // 25 MB per file

type RowStatus = "queued" | "uploading" | "extracting" | "done" | "failed";

interface IntakeRow {
  id:           string;        // local row id
  file:         File;
  status:       RowStatus;
  receiptId?:   string;        // pending_receipts.receipt_id once inserted
  errorMsg?:    string;
  extracted?: {
    supplier?:   string;
    total?:      number;
    confidence?: number;
  };
}

// -----------------------------------------------------------------------------

export default function ApIntakeScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<IntakeRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!user) return null;
  if (!hasRole(user, "manager")) {
    return (
      <div className="px-6 md:px-10 py-6 md:py-8">
        <Card className="max-w-xl mx-auto p-6">
          <EmptyState
            icon={AlertTriangle}
            title="Manager access only"
            description="Bulk PDF intake is a finance/admin function — restricted to managers and above. Field staff use the receipt camera capture instead."
          />
        </Card>
      </div>
    );
  }

  // -------- File handling ----------------------------------------------------
  async function addFiles(files: FileList | File[]) {
    // 8 Oct 2026 — a JPEG dropped here gets the same shrink as a phone snap,
    // so the model never sees an image over its 10 MB (base64) limit.
    const incoming = await Promise.all(Array.from(files).map(f => shrinkImage(f)));
    const accepted: IntakeRow[] = [];
    const skipped: string[] = [];

    for (const f of incoming) {
      // Accept PDFs and images (managers occasionally have a JPEG receipt too)
      const ok = f.type === "application/pdf" || f.type.startsWith("image/");
      if (!ok) { skipped.push(`${f.name} (unsupported type ${f.type})`); continue; }
      if (f.size > MAX_FILE_BYTES) { skipped.push(`${f.name} (>${MAX_FILE_BYTES / 1024 / 1024} MB)`); continue; }
      accepted.push({
        id: crypto.randomUUID(),
        file: f,
        status: "queued",
      });
    }

    setRows(prev => {
      const combined = [...prev, ...accepted];
      if (combined.length > MAX_BATCH) {
        alert(`Hard cap is ${MAX_BATCH} files per batch. Trimming to ${MAX_BATCH}.`);
        return combined.slice(0, MAX_BATCH);
      }
      return combined;
    });

    if (skipped.length > 0) {
      alert(`Skipped:\n${skipped.join("\n")}`);
    }
  }

  function removeRow(id: string) {
    setRows(prev => prev.filter(r => r.id !== id));
  }

  function clearAll() {
    if (rows.some(r => r.status === "uploading" || r.status === "extracting")) {
      if (!confirm("Some files are still processing. Clear anyway?")) return;
    }
    setRows([]);
  }

  // -------- Process each row sequentially -----------------------------------
  // Sequential keeps the UI clear + caps Anthropic concurrency. 50 invoices
  // x ~3 sec each = 2-3 minutes for a full batch. Acceptable for weekly use.
  async function startProcessing() {
    setProcessing(true);
    try {
      for (const row of rows) {
        if (row.status === "done" || row.status === "failed") continue;
        await processOne(row.id);
      }
    } finally {
      setProcessing(false);
    }
  }

  async function processOne(rowId: string) {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;

    const updateRow = (patch: Partial<IntakeRow>) => {
      setRows(prev => prev.map(r => (r.id === rowId ? { ...r, ...patch } : r)));
    };

    try {
      updateRow({ status: "uploading" });

      // 1. Upload bytes to receipt-captures bucket. Path uses uuid prefix so
      //    the manager's bulk dump doesn't clash with anyone's mobile snaps.
      const ext = (row.file.name.split(".").pop() || "pdf").toLowerCase();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const path = `bulk/${user.user_id}/${stamp}-${row.id.slice(0, 8)}.${ext}`;

      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, row.file, {
        contentType: row.file.type,
        cacheControl: "3600",
        upsert: false,
      });
      if (upErr) throw new Error(`Upload failed: ${upErr.message}`);

      // 2. Insert pending_receipts row. captured_by_employee_id required —
      //    manager doing the upload is the "capturer" for bulk intake.
      const { data: ins, error: rowErr } = await supabase
        .from("pending_receipts")
        .insert({
          captured_by_employee_id: user.employee_id,
          storage_path:   path,
          mime_type:      row.file.type,
          size_bytes:     row.file.size,
          notes:          `Bulk intake — original filename: ${row.file.name}`,
        })
        .select("receipt_id")
        .single();
      if (rowErr || !ins) {
        // Best-effort cleanup of the orphaned upload
        await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
        throw new Error(`DB insert failed: ${rowErr?.message ?? "unknown"}`);
      }

      updateRow({ receiptId: ins.receipt_id, status: "extracting" });

      // 3. Fire AI extraction
      const aiRes = await apiFetch("/ai/extract-document", {
        method: "POST",
        body: JSON.stringify({ pending_receipt_id: ins.receipt_id }),
      });
      if (!aiRes.ok) {
        const err = await aiRes.json().catch(() => ({}));
        throw new Error(`AI extraction failed: ${err.detail ?? err.error ?? aiRes.status}`);
      }
      const aiPayload = await aiRes.json();

      updateRow({
        status: "done",
        extracted: {
          supplier:   aiPayload.extracted?.supplier_name,
          total:      aiPayload.extracted?.total_inc_gst,
          confidence: aiPayload.confidence,
        },
      });
    } catch (e: any) {
      console.error("[intake] row failed", e);
      updateRow({ status: "failed", errorMsg: e?.message ?? String(e) });
    }
  }

  // -------- Derived ---------------------------------------------------------
  const counts = {
    total:      rows.length,
    queued:     rows.filter(r => r.status === "queued").length,
    inFlight:   rows.filter(r => r.status === "uploading" || r.status === "extracting").length,
    done:       rows.filter(r => r.status === "done").length,
    failed:     rows.filter(r => r.status === "failed").length,
  };
  const allDone = rows.length > 0 && counts.queued === 0 && counts.inFlight === 0;
  const canStart = rows.length > 0 && counts.queued > 0 && !processing;

  return (
    <div className="px-6 md:px-10 py-6 md:py-8 pb-20">
      <Link to="/ap/receipts" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Pending receipts
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-[20px] font-semibold text-text flex items-center gap-2">
            <Upload size={20} className="text-accent" />
            AP Bulk Intake
          </h1>
          <div className="text-[12px] text-text-muted mt-0.5">
            Drop your weekly accounting pile. PDFs and images both work — Claude reads each one and extracts supplier, ABN, totals, GST, line items.
            {" "}Hard cap: {MAX_BATCH} files per batch.
          </div>
        </div>

        <div className="flex items-center gap-2">
          {rows.length > 0 && (
            <Button variant="secondary" size="sm" icon={Trash2} onClick={clearAll}>
              Clear
            </Button>
          )}
          {canStart && (
            <Button icon={Sparkles} onClick={startProcessing}>
              Process {counts.queued} file{counts.queued === 1 ? "" : "s"}
            </Button>
          )}
          {allDone && (
            <Link to="/ap/receipts">
              <Button icon={FolderOpen}>Review in queue</Button>
            </Link>
          )}
        </div>
      </div>

      {/* Counts strip — only when something is in the list */}
      {rows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <CountTile label="Total"     value={counts.total} />
          <CountTile label="Queued"    value={counts.queued} />
          <CountTile label="Done"      value={counts.done}     tone={counts.done > 0 ? "ok" : undefined} />
          <CountTile label="Failed"    value={counts.failed}   tone={counts.failed > 0 ? "danger" : undefined} />
        </div>
      )}

      {/* Drop zone */}
      <Card
        className={`mb-4 transition-colors ${
          dragOver ? "border-accent bg-accent/5" : ""
        }`}
      >
        <div
          className="p-8 text-center"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
          }}
        >
          <Upload size={32} className="text-text-muted mx-auto mb-2" />
          <div className="text-[14px] text-text font-medium mb-1">
            Drag PDFs (or images) here
          </div>
          <div className="text-[12px] text-text-muted mb-3">
            Or click to pick files. Up to {MAX_BATCH} at a time, {MAX_FILE_BYTES / 1024 / 1024} MB each.
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={FileText}
            onClick={() => fileInputRef.current?.click()}
          >
            Pick files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      </Card>

      {/* File list */}
      {rows.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line">
                  {["File", "Size", "Status", "Supplier", "Total", "Confidence", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className={`border-b border-line last:border-b-0 ${i % 2 ? "bg-ink/40" : ""}`}>
                    <td className="px-4 py-2.5 text-text truncate max-w-[280px]" title={r.file.name}>
                      {r.file.name}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-text-muted">
                      {(r.file.size / 1024).toFixed(0)} KB
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={r.status} errorMsg={r.errorMsg} />
                    </td>
                    <td className="px-4 py-2.5 text-text-muted">
                      {r.extracted?.supplier ?? <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-text">
                      {r.extracted?.total != null
                        ? `$${Number(r.extracted.total).toFixed(2)}`
                        : <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {r.extracted?.confidence != null
                        ? <Pill
                            label={`${Math.round(r.extracted.confidence * 100)}%`}
                            tone={r.extracted.confidence >= 0.85 ? "ok" : r.extracted.confidence >= 0.5 ? "warning" : "critical"}
                            small
                          />
                        : <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {r.status === "queued" && (
                        <button
                          onClick={() => removeRow(r.id)}
                          className="text-text-muted hover:text-danger p-1 rounded"
                          title="Remove from batch"
                        >
                          <XCircle size={12}/>
                        </button>
                      )}
                      {r.receiptId && r.status === "done" && (
                        <Link
                          to="/ap/receipts"
                          className="text-[10px] text-accent hover:underline"
                        >
                          → Queue
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Footer note */}
      <div className="mt-4 text-[11px] text-text-dim leading-snug">
        Each AI extraction costs roughly 1c. {counts.done > 0 && <>Tonight's batch so far: ~${(counts.done * 0.01).toFixed(2)} estimated.</>}
        {" "}After processing, head to <Link to="/ap/receipts" className="text-accent hover:underline">Pending receipts</Link> to confirm / code each one.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function CountTile({ label, value, tone }: { label: string; value: number; tone?: "ok" | "danger" }) {
  const colour = tone === "ok" ? "text-ok" : tone === "danger" ? "text-danger" : "text-text";
  return (
    <div className="bg-ink-raised border border-line rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`text-[18px] font-mono font-semibold mt-1 ${colour}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status, errorMsg }: { status: RowStatus; errorMsg?: string }) {
  switch (status) {
    case "queued":
      return <Pill label="queued" tone="default" small />;
    case "uploading":
      return (
        <span className="inline-flex items-center gap-1 text-[11px] text-info">
          <Loader2 size={11} className="animate-spin"/> uploading
        </span>
      );
    case "extracting":
      return (
        <span className="inline-flex items-center gap-1 text-[11px] text-accent">
          <Sparkles size={11} className="animate-pulse"/> AI reading
        </span>
      );
    case "done":
      return (
        <span className="inline-flex items-center gap-1 text-[11px] text-ok">
          <CheckCircle2 size={11}/> done
        </span>
      );
    case "failed":
      return (
        <span className="inline-flex items-center gap-1 text-[11px] text-danger" title={errorMsg}>
          <XCircle size={11}/> failed
        </span>
      );
  }
}
